import React, { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Pencil, Undo2, Check, Trash2, X } from 'lucide-react';
import { AreaMap } from '../components/AreaMap';
import { Button } from '../components/ui/button';

L.drawLocal.draw.handlers.polygon.tooltip = { start: 'Marca el primer punto del límite.', cont: 'Añade otro punto.', end: 'Cierra el contorno en el primer punto.' };
L.drawLocal.edit.handlers.edit.tooltip = { text: 'Arrastra los puntos del límite.', subtext: 'Pulsa Listo al terminar.' };

const DrawingLayer = ({ geometry, onChange, onReady, setMode }) => {
  const map = useMap();
  const latest = useRef(onChange);
  latest.current = onChange;
  const layers = useRef(null);
  const key = JSON.stringify(geometry);
  useEffect(() => {
    const group = L.featureGroup().addTo(map);
    layers.current = group;
    const draw = new L.Draw.Polygon(map, { allowIntersection: false, showArea: false, shapeOptions: { color: '#EE3B36', weight: 3 }, drawError: { color: '#DC2626', message: 'Los límites no deben cruzarse.' } });
    const edit = new L.EditToolbar.Edit(map, { featureGroup: group, selectedPathOptions: { maintainColor: true } });
    const stop = () => { draw.disable(); edit.disable(); setMode(null); };
    const created = (event) => { group.clearLayers(); group.addLayer(event.layer); latest.current(event.layer.toGeoJSON().geometry); setMode(null); };
    map.on(L.Draw.Event.CREATED, created);
    onReady({ draw: () => { stop(); draw.enable(); setMode('draw'); }, edit: () => { stop(); edit.enable(); setMode('edit'); },
      undo: () => draw.deleteLastVertex(), finish: () => { if (draw.enabled()) draw.completeShape(); else { edit.save(); latest.current(group.getLayers()[0]?.toGeoJSON().geometry || null); stop(); } },
      clear: () => { stop(); group.clearLayers(); latest.current(null); }, cancel: () => { if (edit.enabled()) edit.revertLayers(); stop(); } });
    return () => { stop(); map.off(L.Draw.Event.CREATED, created); map.removeLayer(group); };
  }, [map, onReady, setMode]);
  useEffect(() => {
    const group = layers.current;
    if (!group) return;
    group.clearLayers();
    if (key && key !== 'null') L.geoJSON(JSON.parse(key), { style: { color: '#EE3B36', weight: 3 } }).eachLayer((layer) => group.addLayer(layer));
  }, [key]);
  return null;
};

export const AreaBoundaryEditor = ({ geometry, onChange, areas, areaId }) => {
  const [tools, setTools] = useState(null);
  const [mode, setMode] = useState(null);
  const [initial] = useState(geometry);
  return <div className="space-y-3 min-w-0">
    <div className="flex flex-wrap gap-2">
      {!mode && <><Button type="button" data-testid="area-draw" variant="outline" onClick={() => tools?.draw()} className="gap-1.5"><Pencil size={14} />Dibujar límites</Button><Button type="button" data-testid="area-edit-boundary" variant="outline" disabled={!geometry} onClick={() => tools?.edit()}>Editar límites</Button></>}
      {mode && <><Button type="button" data-testid="area-finish" onClick={() => tools?.finish()} className="gap-1.5"><Check size={14} />Listo</Button>{mode === 'draw' && <Button type="button" data-testid="area-undo" variant="outline" title="Deshacer punto" onClick={() => tools?.undo()}><Undo2 size={15} /></Button>}<Button type="button" data-testid="area-cancel" variant="outline" title="Cancelar edición" onClick={() => tools?.cancel()}><X size={15} /></Button></>}
      <Button type="button" data-testid="area-clear" variant="outline" title="Eliminar límites" disabled={!geometry && !mode} onClick={() => tools?.clear()}><Trash2 size={15} /></Button>
    </div>
    <AreaMap testId="area-boundary-map" areas={areas.filter((a) => a.id !== areaId)} geometry={initial} className="h-[360px]">
      <DrawingLayer geometry={geometry} onChange={onChange} onReady={setTools} setMode={setMode} />
    </AreaMap>
    <p data-testid="area-boundary-status" className="text-xs text-gray-500">{geometry ? `Límites definidos · ${geometry.coordinates[0].length - 1} puntos` : 'Sin límites en el mapa'}</p>
  </div>;
};